import React, { useState } from 'react';
import { Pencil, Trash2, ExternalLink, Info } from 'lucide-react';
import type { CurrentPlan } from '../../types/data';
import { GenericTable } from '../GenericTable/GenericTable';
import { Modal } from '../Modal';

interface CurrentPlanListProps {
  currentPlans: CurrentPlan[];
  savePlan: (currentPlan: CurrentPlan) => void;
  onEdit: (currentPlan: CurrentPlan) => void;
  onDelete: (id: string) => void;
}

export const CurrentPlanList: React.FC<CurrentPlanListProps> = ({
  currentPlans,
  savePlan,
  onEdit,
  onDelete,
}) => {
  const [modalContent, setModalContent] = useState<string | null>(null);

  const handleToggleEnabled = (currentPlan: CurrentPlan, enabled: boolean) => {
    const updatedPlan = { ...currentPlan, enabled };
    savePlan(updatedPlan);
  };

  const handleDateChange = (currentPlan: CurrentPlan, start_date: string) => {
    const updatedPlan = { ...currentPlan, start_date };
    savePlan(updatedPlan);
  };

  const openNotesModal = (notes: string) => {
    setModalContent(notes);
  };

  const closeNotesModal = () => {
    setModalContent(null);
  };

  if (currentPlans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No current plans found. Create one to get started.
      </div>
    );
  }

  return (
    <div>
      <GenericTable
        data={currentPlans}
        columns={[
          {
            key: 'enabled',
            label: 'Enabled',
            render: (item) => (
              <input
                type="checkbox"
                checked={item.enabled}
                onChange={(e) => handleToggleEnabled(item, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            ),
          },
          {
            key: 'start_date',
            label: 'Start Date',
            render: (item) => (
              <input
                type="date"
                value={item.start_date}
                onChange={(e) => handleDateChange(item, e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            ),
          },
          { key: 'name', label: 'Name', sticky: 'left' },
          { key: 'type', label: 'Type' },
          {
            key: 'dosage',
            label: 'Dosage',
            render: (item) =>
              item.dosage && item.dosage_units
                ? `${item.dosage} ${item.dosage_units}`
                : 'N/A',
          },
          { key: 'timing', label: 'Timing' },
          {
            key: 'frequency_interval',
            label: 'Frequency Interval',
            render: (item) => item.frequency_interval ?? 'N/A',
          },
          {
            key: 'doses_per_interval',
            label: 'Doses per Interval',
            render: (item) => item.doses_per_interval ?? 'N/A',
          },
          {
            key: 'days_on_off',
            label: 'Days On / Off',
            render: (item) =>
              `${item.days_on ?? 'N/A'} / ${item.days_off ?? 'N/A'}`,
          },
          {
            key: 'duration_repeat',
            label: 'Duration / Repeat',
            render: (item) =>
              `${item.duration_period ?? 'N/A'} / ${item.duration_repeat || '0 days'}`,
          },
          {
            key: 'dosage_level',
            label: 'Dosage Level',
            render: (item) => item.dosage_level ?? 'unknown',
          },
          {
            key: 'dosage_type',
            label: 'Dosage Type',
            render: (item) => item.dosage_type ?? 'unknown',
          },
          {
            key: 'notes',
            label: 'Notes',
            render: (item) =>
              item.notes ? (
                <button
                  onClick={() => openNotesModal(item.notes)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="View notes"
                >
                  <Info className="w-5 h-5" />
                </button>
              ) : (
                'N/A'
              ),
          },
          {
            key: 'source',
            label: 'Source',
            render: (item) =>
              item.source ? (
                <a
                  href={item.source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ExternalLink />
                </a>
              ) : (
                'N/A'
              ),
          },
          {
            key: 'actions',
            label: 'Actions',
            sticky: 'right',
            render: (item) => (
              <div className="flex gap-3">
                <button
                  onClick={() => onEdit(item)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="Edit current plan"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onDelete(item.id!)}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete current plan"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ),
          },
        ]}
      />

      <Modal isOpen={!!modalContent} onClose={closeNotesModal} title="Notes">
        <p className="text-gray-700">{modalContent}</p>
      </Modal>
    </div>
  );
};
